import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth } from 'config/firebase';
import { usersService } from 'services/firestore';

// Reused across every Google sign-in attempt. We request the basic profile
// scope (email + name + photo) and prompt the chooser every time so users on
// shared computers can pick the right account.
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          await usersService.createUserProfile(user);
        } catch (err) {
          // Non-blocking — don't prevent login if profile sync fails
          console.error('Failed to sync user profile:', err);
        }
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signup = useCallback(async (email, password, displayName) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) {
      await updateProfile(userCredential.user, { displayName });
    }
    return userCredential;
  }, []);

  const login = useCallback((email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  }, []);

  // Google sign-in via popup. Returns a UserCredential just like login() so
  // call sites stay symmetric. Pop-up blockers / closes throw codes the
  // caller can map (auth/popup-closed-by-user, auth/popup-blocked, etc.).
  const loginWithGoogle = useCallback(() => {
    return signInWithPopup(auth, googleProvider);
  }, []);

  const logout = useCallback(() => {
    return signOut(auth);
  }, []);

  const resetPassword = useCallback((email) => {
    return sendPasswordResetEmail(auth, email);
  }, []);

  const value = useMemo(() => ({
    currentUser,
    loading,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword
  }), [currentUser, loading, signup, login, loginWithGoogle, logout, resetPassword]);

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

